import React, { useEffect, useState } from "react";
import { BarChart3, Users, Eye, ShoppingCart, TrendingUp, Receipt, Landmark, Mail } from "lucide-react";
import api from "../../lib/api";
import { NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp } from "../../lib/format";

const Stat = ({ icon: Icon, label, value, color = "cyan" }) => (
  <NeonCard variant="flow" innerClassName="p-4">
    <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs uppercase tracking-widest"><Icon className={`h-4 w-4 text-[var(--neon-${color})]`} /> {label}</div>
    <p className="mt-2 font-display text-2xl text-white">{value}</p>
  </NeonCard>
);

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [a, setA] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [vat, setVat] = useState(null);
  const [tax, setTax] = useState(null);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/analytics"), api.get("/admin/traffic"), api.get("/admin/vat"),
      api.get("/admin/tax-estimate"), api.get("/admin/email-health"),
    ]).then(([an, tr, v, tx, em]) => {
      if (an.status === "fulfilled") setA(an.value.data);
      if (tr.status === "fulfilled") setTraffic(tr.value.data);
      if (v.status === "fulfilled") setVat(v.value.data);
      if (tx.status === "fulfilled") setTax(tx.value.data);
      if (em.status === "fulfilled") setEmail(em.value.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  const sum = traffic?.summary || {};
  const periods = vat?.periods || {};
  const emailColor = email?.status === "green" ? "green" : email?.status === "amber" ? "gold" : "pink";

  return (
    <div data-testid="admin-analytics" className="max-w-5xl">
      <div className="flex items-center gap-2"><BarChart3 className="h-6 w-6 text-[var(--neon-cyan)]" /><h2 className="font-display uppercase text-2xl text-white">Analytics &amp; Finance</h2></div>

      {/* Funnel */}
      <p className="mt-5 text-xs uppercase tracking-widest text-[var(--text-muted)]">Conversion funnel</p>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={Eye} label="Page views" value={a?.page_views ?? 0} />
        <Stat icon={Users} label="Visitors" value={a?.unique_visitors ?? 0} />
        <Stat icon={ShoppingCart} label="Add to cart" value={a?.add_to_cart ?? 0} color="gold" />
        <Stat icon={TrendingUp} label="Purchases" value={a?.purchases ?? 0} color="green" />
        <Stat icon={ShoppingCart} label="Abandoned" value={a?.abandoned_carts ?? 0} color="pink" />
        <Stat icon={TrendingUp} label="Conversion" value={`${((a?.conversion_rate ?? 0) * 100).toFixed(1)}%`} color="green" />
      </div>

      {/* Traffic periods */}
      <p className="mt-6 text-xs uppercase tracking-widest text-[var(--text-muted)]">Traffic</p>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {["today", "week", "month", "six_months", "year", "lifetime"].map((k) => (
          <NeonCard key={k} variant="cyan" innerClassName="p-3">
            <p className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{k.replace("_", " ")}</p>
            <p className="mt-1 text-white font-display text-lg">{sum[k]?.page_views ?? 0} <span className="text-[10px] text-[var(--text-muted)]">views</span></p>
            <p className="text-[var(--neon-cyan)] text-xs">{sum[k]?.unique_visitors ?? 0} visitors</p>
          </NeonCard>
        ))}
      </div>

      {/* VAT */}
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <NeonCard variant="flow" innerClassName="p-5">
          <div className="flex items-center gap-2"><Receipt className="h-5 w-5 text-[var(--neon-gold)]" /><p className="font-display uppercase text-white">VAT ({vat?.vat_rate_pct ?? 20}%)</p></div>
          <p className="mt-1 text-[11px] text-[var(--text-muted)]">{vat?.basis}</p>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-[var(--text-muted)] text-[11px] uppercase"><th className="text-left font-normal">Period</th><th className="text-right font-normal">Gross</th><th className="text-right font-normal">VAT due</th></tr></thead>
            <tbody>
              {["today", "week", "month", "quarter", "year", "lifetime"].map((k) => periods[k] && (
                <tr key={k} className="border-t border-white/5"><td className="py-1.5 capitalize text-white">{k}</td><td className="text-right text-[var(--text-muted)]">{gbp(periods[k].gross_sales)}</td><td className="text-right text-[var(--neon-gold)]">{gbp(periods[k].vat_due)}</td></tr>
              ))}
            </tbody>
          </table>
        </NeonCard>

        {/* Tax estimate */}
        <NeonCard variant="flow" innerClassName="p-5">
          <div className="flex items-center gap-2"><Landmark className="h-5 w-5 text-[var(--neon-green)]" /><p className="font-display uppercase text-white">Tax estimate</p></div>
          <p className="mt-2 text-sm text-white">Net profit: <span className="text-[var(--neon-green)]">{gbp(tax?.net_profit || 0)}</span></p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-white/10 p-3">
              <p className="text-[11px] uppercase text-[var(--text-muted)]">Sole trader</p>
              <p className="mt-1 text-white">Tax+NI: {gbp(tax?.sole_trader?.total || 0)}</p>
              <p className="text-[var(--neon-green)]">Take home: {gbp(tax?.sole_trader?.take_home || 0)}</p>
            </div>
            <div className="rounded-lg border border-white/10 p-3">
              <p className="text-[11px] uppercase text-[var(--text-muted)]">Ltd company</p>
              <p className="mt-1 text-white">Corp tax: {gbp(tax?.ltd_company?.corporation_tax || 0)}</p>
              <p className="text-[var(--neon-green)]">Retained: {gbp(tax?.ltd_company?.retained || 0)}</p>
            </div>
          </div>
          <p className="mt-3 text-[10px] text-[var(--text-muted)]">{tax?.disclaimer}</p>
        </NeonCard>
      </div>

      {/* Email health */}
      <NeonCard variant="soft" className="mt-4" innerClassName="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-white">Email health</p></div>
          <Pill color={emailColor}>{(email?.status || "unknown").toUpperCase()}</Pill>
        </div>
        <p className="mt-2 text-sm text-[var(--text-muted)]">{email?.message}</p>
        <div className="mt-2 flex gap-4 text-xs text-[var(--text-muted)]"><span>Delivered: <span className="text-[var(--neon-green)]">{email?.delivered ?? 0}</span></span><span>Failed: <span className="text-[var(--neon-pink)]">{email?.failed ?? 0}</span></span><span>Recent: {email?.recent_count ?? 0}</span></div>
      </NeonCard>
    </div>
  );
}
